import { Copy, UserPlus } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { Modal } from "../components/Modal";
import { useApp } from "../context/AppContext";
import { api, rows } from "../lib/api";
import type { ApiList, Membership } from "../lib/types";

const roles = ["OWNER", "ADMIN", "MANAGER", "PROJECT_MANAGER", "ACCOUNTANT", "MEMBER", "VIEWER"];
const inviteRoles = roles.filter((role) => role !== "OWNER");

type Invitation = {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
};
type InviteResult = Invitation & { invite_url: string; qr_svg_data_url: string };

export function TeamPage() {
  const { bootstrap } = useApp();
  const [members, setMembers] = useState<Membership[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [federated, setFederated] = useState(false);
  const [open, setOpen] = useState(false);
  const [shared, setShared] = useState<InviteResult | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", role: "MEMBER" });

  async function load() {
    try {
      setMembers(rows(await api.get<ApiList<Membership>>("/api/v1/enterprises/memberships/")));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Chargement impossible.");
    }
  }

  useEffect(() => {
    void load();
    fetch("/api/v1/auth/oidc/status/", { credentials: "same-origin" })
      .then((res) => res.ok ? res.json() as Promise<{ enabled: boolean; available: boolean }> : Promise.reject(new Error("Identity inaccessible")))
      .then(({ enabled, available }) => setFederated(enabled && available))
      .catch(() => setError("Statut de connexion centrale indisponible."));
  }, []);

  useEffect(() => {
    if (!federated) return;
    void api.get<Invitation[]>("/api/v1/auth/oidc/invitations/manage/")
      .then(setInvitations)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Invitations indisponibles."));
  }, [federated, bootstrap?.active_organisation?.code]);

  async function invite(event: FormEvent) {
    event.preventDefault();
    setError("");
    setShared(null);
    try {
      if (federated) {
        const result = await api.post<InviteResult>("/api/v1/auth/oidc/invitations/manage/", form);
        setShared(result);
        setNotice("Invitation créée. La personne devra confirmer avec son compte SahelTech.");
        setInvitations(await api.get<Invitation[]>("/api/v1/auth/oidc/invitations/manage/"));
      } else {
        const result = await api.post<{ onboarding_required: boolean }>("/api/v1/enterprises/memberships/invite/", form);
        setNotice(result.onboarding_required ? "Invitation envoyée par e-mail." : "Membre ajouté à l'entreprise.");
      }
      setOpen(false);
      setForm({ email: "", role: "MEMBER" });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Invitation impossible.");
    }
  }

  async function revoke(invitation: Invitation) {
    setError("");
    try {
      await api.delete("/api/v1/auth/oidc/invitations/manage/" + invitation.id + "/");
      setInvitations(await api.get<Invitation[]>("/api/v1/auth/oidc/invitations/manage/"));
      if (shared?.id === invitation.id) setShared(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Annulation impossible.");
    }
  }

  async function update(member: Membership, patch: Partial<Membership>) {
    setError("");
    try {
      await api.patch("/api/v1/enterprises/memberships/" + member.id + "/", patch);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Modification impossible.");
    }
  }

  const canInviteAdmin = bootstrap?.membership?.role === "OWNER";

  return <div>
    <div className="flex items-end justify-between gap-4">
      <div>
        <div className="text-sm font-semibold text-emerald-700">Accès entreprise</div>
        <h1 className="mt-1 text-3xl font-black">Équipe & rôles</h1>
        <p className="mt-2 text-sm text-slate-500">Les droits appartiennent à l’entreprise, jamais au compte Google.</p>
      </div>
      <button className="btn-primary" onClick={() => setOpen(true)}><UserPlus size={16}/> Inviter un membre</button>
    </div>
    {error && <p role="alert" className="mt-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}
    {notice && <p className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900">{notice}</p>}

    {shared && <section className="panel mt-6 grid gap-5 p-5 sm:grid-cols-[170px_1fr]">
      <img src={shared.qr_svg_data_url} alt="QR d'invitation de l'entreprise" className="h-40 w-40 rounded-xl border border-slate-200" />
      <div className="space-y-3">
        <h2 className="font-black">Inviter {shared.email}</h2>
        <p className="text-sm text-slate-600">Scannez ce QR puis continuez avec SahelTech Compte, y compris Google. L’accès sera accordé uniquement à l’adresse invitée, au rôle choisi.</p>
        <button className="btn-secondary" onClick={() => void navigator.clipboard.writeText(shared.invite_url)}>
          <Copy size={16}/> Copier le lien d'invitation
        </button>
        <p className="text-xs text-slate-500">Ce QR n'est affiché qu'à sa création. L'invitation expire après 72 heures.</p>
      </div>
    </section>}

    <div className="panel mt-6 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
          <th className="px-5 py-3">Utilisateur</th><th className="px-5 py-3">Rôle</th><th className="px-5 py-3">État</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{members.map((m) => <tr key={m.id}>
          <td className="px-5 py-4 font-semibold">{m.username}</td>
          <td className="px-5 py-4"><select className="field max-w-56" value={m.role} onChange={(e) => void update(m, { role: e.target.value })}>
            {roles.map(r => <option key={r}>{r}</option>)}
          </select></td>
          <td className="px-5 py-4"><button className={`badge ${m.actif ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} onClick={() => void update(m, { actif: !m.actif })}>{m.actif ? "Actif" : "Inactif"}</button></td>
        </tr>)}</tbody>
      </table>
    </div>

    {federated && <div className="panel mt-6 p-5">
      <h2 className="font-black">Invitations en attente</h2>
      {invitations.length === 0 && <p className="mt-3 text-sm text-slate-500">Aucune invitation.</p>}
      {invitations.map((inv) => <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 py-3 text-sm">
        <div><span className="font-semibold">{inv.email}</span> · {inv.role} · {inv.status}</div>
        {inv.status === "pending" && <button className="btn-secondary" onClick={() => void revoke(inv)}>Révoquer</button>}
      </div>)}
    </div>}

    <Modal open={open} title={federated ? "Invitation QR SahelTech" : "Ajouter un membre"} onClose={() => setOpen(false)}>
      <form onSubmit={invite} className="space-y-4">
        <label><span className="label">E-mail du destinataire</span><input type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></label>
        <label><span className="label">Rôle dans l'entreprise</span>
          <select className="field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {(federated ? inviteRoles : roles).map(role => <option key={role} disabled={federated && role === "ADMIN" && !canInviteAdmin}>{role}</option>)}
          </select>
        </label>
        <p className="text-xs text-slate-500">{federated ? "Aucun compte ni accès entreprise n'est créé avant acceptation." : "Mode historique, en attente de migration Identity."}</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>Annuler</button>
          <button className="btn-primary">Créer l'invitation</button>
        </div>
      </form>
    </Modal>
  </div>;
}
